import React, { useContext, useEffect, useState } from "react";
import EditableUserName from "./EditableUserName";
import { UserContext } from "../../../../../../UserContext.js";
import { progressService } from "../../../../../../services/progressService.js";

// 🔥 Deep unwrap nested payload/data structures
const unwrapPayload = (node) => {
  let current = node;
  let depth = 0;
  const maxDepth = 10; // Prevent infinite loops

  while (
    current &&
    typeof current === "object" &&
    (current.payload || current.data) &&
    depth < maxDepth
  ) {
    current = current.payload || current.data;
    depth++;
  }
  
  return current;
};

const ProfileCard = ({
  userId,
  formData: externalFormData,
  onInputChange,
  headerLabel = "Current State",
  editableName = true,
  showKnownFor = true,
  showCoreValues = false,
  coreValues = [],
}) => {
  const { user } = useContext(UserContext);
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Fetch data when userId changes
  useEffect(() => {
    const loadProfile = async () => {
      // Only fetch if userId is provided
      if (!userId) {
        console.log("⚠️ ProfileCard: No userId provided, skipping fetch");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log("🔵 ProfileCard: Starting fetch for userId:", userId);

        // API call
        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "identity",
          "current_identity_reflection"
        );

        console.log("📦 ProfileCard: Full API Response:", JSON.stringify(res, null, 2));

        // Try multiple possible response structures
        let payload = null;
        
        // Try different paths to find the data
        if (res?.data?.payload) {
          payload = unwrapPayload(res.data.payload);
          console.log("✅ Found data at res.data.payload");
        } else if (res?.payload) {
          payload = unwrapPayload(res.payload);
          console.log("✅ Found data at res.payload");
        } else if (res?.data?.data) {
          payload = unwrapPayload(res.data.data);
          console.log("✅ Found data at res.data.data");
        } else if (res?.data) {
          payload = unwrapPayload(res.data);
          console.log("✅ Found data at res.data");
        } else {
          payload = unwrapPayload(res);
          console.log("✅ Found data at root level");
        }

        console.log("🎯 ProfileCard: Unwrapped Payload:", JSON.stringify(payload, null, 2));

        if (!payload || typeof payload !== 'object') {
          console.warn("⚠️ ProfileCard: No valid payload found");
          setFetchedData(null);
          return;
        }

        // Extract and clean the data
        const cleaned = {
          userName: payload.userName || payload.name || "",
          profileImage: payload.profileImage || payload.image || payload.avatar || "",
          currentTitle: payload.currentTitle || payload.title || payload.position || "",
          company: payload.company || payload.companyName || "",
          location: payload.location || payload.city || "",
          salary: payload.salary || payload.currentSalary || "",
          experienceYears: payload.experienceYears || payload.yearsOfExperience || "",
          experienceMonths: payload.experienceMonths || payload.monthsOfExperience || "",
          technicalSkills: payload.technicalSkills || payload.skills || "",
          behaviors: payload.behaviors || payload.behavior || "",
          advice: payload.advice || payload.bestAdvice || "",
          hobbies: payload.hobbies || payload.interests || "",
        };

        console.log("✅ ProfileCard: Cleaned Data:", JSON.stringify(cleaned, null, 2));

        setFetchedData(cleaned);
      } catch (error) {
        console.error("❌ ProfileCard: Fetch Error:", error);
        console.error("Error details:", {
          message: error.message,
          stack: error.stack,
          response: error.response
        });
        setError(error.message);
        setFetchedData(null);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId]);

  // ✅ Determine which data to use
  const formData = React.useMemo(() => {
    console.log("🔄 ProfileCard: Computing formData", {
      hasFetchedData: !!fetchedData,
      hasExternalData: !!externalFormData,
      fetchedData,
      externalFormData
    });

    // If we have fetched data, use it
    if (fetchedData) {
      return fetchedData;
    }

    // Otherwise use external data
    if (externalFormData) {
      return externalFormData;
    }

    return null;
  }, [fetchedData, externalFormData]);

  const safeFormData = formData || {};

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.lastName || "";

  console.log("🎨 ProfileCard: Render State", {
    loading,
    error,
    hasFormData: !!formData,
    formData: safeFormData,
    userName
  });

  // ✅ Loading state
  if (loading) {
    return (
      <div className="lg:col-span-4 bg-white p-[1px] rounded-[25px] min-w-[400px] max-w-[429px]">
        <div className="bg-white rounded-[25px] border-[1.5px] border-[#707070] overflow-hidden flex flex-col items-center justify-center h-[600px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-500 text-sm font-medium">Loading profile data...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (error && !formData) {
    return (
      <div className="lg:col-span-4 bg-white p-[1px] rounded-[25px] min-w-[400px] max-w-[429px]">
        <div className="bg-white rounded-[25px] border-[1.5px] border-[#707070] overflow-hidden flex flex-col items-center justify-center h-[600px] p-8">
          <p className="text-red-500 text-sm font-medium mb-2">Failed to load profile</p>
          <p className="text-gray-400 text-xs">{error}</p>
        </div>
      </div>
    );
  }

  // ✅ Determine if skeleton should show
  const hasAnyData = 
    safeFormData.currentTitle ||
    safeFormData.location ||
    safeFormData.salary ||
    safeFormData.experienceYears ||
    safeFormData.experienceMonths ||
    safeFormData.technicalSkills ||
    safeFormData.behaviors ||
    safeFormData.advice ||
    safeFormData.hobbies;

  const isSkeleton = !hasAnyData;

  const Shimmer = ({ className = "w-full h-3" }) => (
    <div className={`rounded-lg bg-gray-200/80 animate-pulse ${className}`} />
  );

  const getValue = (value, fallback = null) => {
    if (isSkeleton) return null;
    return value || fallback;
  };

  const knownForItems = [
    safeFormData?.technicalSkills,
    safeFormData?.behaviors,
    safeFormData?.advice,
    safeFormData?.hobbies,
  ].filter(Boolean);
  
  const isKnownForEmpty = knownForItems.length === 0;

  return (
    <div className="lg:col-span-4 bg-white p-[1px] rounded-[25px] min-w-[400px] max-w-[429px]">
      <div className="bg-white rounded-[25px] border-[1.5px] border-[#707070] overflow-hidden flex flex-col items-center">
        {/* Header */}
        <div className="bg-[#0D1111] w-full rounded-t-[25px] flex flex-col items-center justify-center py-2 2xl:py-6 relative">
          <p className="text-white text-xs font-gotham tracking-[0.15em] uppercase">
            {headerLabel}
          </p>

          {/* Profile Picture */}
          <div className="relative mt-6">
            <div className="2xl:w-[164px] w-[125px] h-[125px] 2xl:h-[164px] rounded-full bg-white border-[6px] border-[#F1F1F1] overflow-hidden flex items-center justify-center -mb-[65px] 2xl:-mb-[82px] shadow-md">
              {safeFormData?.profileImage ? (
                <img
                  src={safeFormData.profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="pt-[70px] 2xl:pt-[90px] pb-8 px-10 flex flex-col items-center text-center w-full">
          {/* Name */}
          {editableName ? (
            <EditableUserName
              userName={safeFormData?.userName || userName || "User Name"}
              onUpdate={(newName) =>
                onInputChange && onInputChange("userName", newName)
              }
            />
          ) : (
            <h2 className="text-center text-lg md:text-[23px] font-gotham font-semibold text-[#1C1C1C] mb-1">
              {safeFormData?.userName || userName || "User Name"}
            </h2>
          )}

          {/* Title */}
          <div className="text-[#777777] text-[14px] font-gotham uppercase tracking-[0.5px] mb-3 2xl:mb-6 w-full flex justify-center">
            {getValue(safeFormData?.currentTitle) ? (
              <p>{safeFormData.currentTitle}</p>
            ) : isSkeleton ? (
              <div className="w-2/3">
                <Shimmer />
              </div>
            ) : (
              <p>SOFTWARE ENGINEER</p>
            )}
          </div>

          {/* Info Section */}
          <div className="w-full border-y border-[#E3E3E3] py-4 text-[14px] font-gotham text-left space-y-3">
            <table className="w-full border-collapse">
              <tbody>
                {[
                  { label: "Location", value: getValue(safeFormData?.location) },
                  { label: "Salary", value: getValue(safeFormData?.salary) },
                  {
                    label: "Total Experience",
                    value:
                      safeFormData?.experienceYears || safeFormData?.experienceMonths
                        ? `${safeFormData.experienceYears || 0} years ${
                            safeFormData.experienceMonths || 0
                          } months`
                        : null,
                  },
                ].map((item, idx) => (
                  <tr key={idx}>
                    <td className="text-[#B3B3B3] text-[12px] uppercase tracking-[1px] font-medium w-[160px] align-top pr-1">
                      {item.label}
                    </td>
                    <td className="text-[#B3B3B3] w-[10px] text-center">:</td>
                    <td className="text-[#323232] text-[14px] font-normal pl-1">
                      {item.value ? (
                        <span>{item.value}</span>
                      ) : (
                        <Shimmer className="w-3/4 h-3" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Known For Section */}
          {showKnownFor && (
            <div className="mt-3 2xl:mt-6 w-full text-left min-h-[120px]">
              <p className="text-center text-[12px] font-bold uppercase text-[#000] mb-4 tracking-[0.5px]">
                Known For
              </p>
              {isKnownForEmpty ? (
                <div className="space-y-3 px-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                      <Shimmer className="h-3 w-full" />
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="list-disc list-inside text-[16px] text-[#323232] leading-[145%] space-y-[3px] font-gotham">
                  {knownForItems.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Core Values Section */}
          {showCoreValues && coreValues && coreValues.length > 0 && (
            <div className="mt-2 2xl:mt-6 w-full">
              <div className="w-full border-t border-[#E3E3E3] py-4" />

              <p className="text-center text-[12px] font-bold uppercase text-[#000] mb-4 tracking-[0.5px]">
                Your Core Values
              </p>

              <div className="flex flex-wrap justify-center gap-2 px-2">
                {coreValues.map((value, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center justify-center px-4 py-2 bg-black rounded-full"
                  >
                    <span
                      style={{
                        fontFamily: "Gotham",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "1.2",
                        color: "#FFFFFF",
                        whiteSpace: "normal",
                        textAlign: "center",
                      }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;